import type { ActivityLogicPlugin } from '../../core/types/plugin.base.types';
import type { ArenaPayload, ArenaRuntime } from '../../core/types/payloads/arena.types';
import { applyStimulusFields } from '../../core/print/blockStimulus';
import type { PluginContext } from '../../core/types/plugin.base.types';
import type { Participant, ScorePatch } from '../../core/types/room.types';
import type { HostActionPayload, StudentInputEnvelope } from '../../core/types/events.types';

export function validateArenaPayload(raw: unknown): ArenaPayload {
  if (!raw || typeof raw !== 'object') throw new Error('Arena payload must be an object');
  const data = raw as Partial<ArenaPayload>;
  if (!data.title) throw new Error('Arena requires title');
  if (!Array.isArray(data.questions) || data.questions.length === 0) {
    throw new Error('Arena requires at least one question');
  }

  return applyStimulusFields(
    {
      title: String(data.title),
      questions: data.questions.map((q, index) => {
      const kind = (q as { kind?: string }).kind || 'multiple-choice';
      const base = {
        id: String((q as { id?: string }).id || `q_${index + 1}`),
        prompt: String((q as { prompt?: string }).prompt || ''),
        kind: kind as 'multiple-choice' | 'true-false' | 'ordering',
        timeLimitSec: (q as { timeLimitSec?: number }).timeLimitSec,
        points: (q as { points?: number }).points ?? 100,
      };
      if (kind === 'true-false') {
        return {
          ...base,
          kind: 'true-false' as const,
          correctAnswer: (q as { correctAnswer?: boolean }).correctAnswer,
        };
      }
      if (kind === 'ordering') {
        return {
          ...base,
          kind: 'ordering' as const,
          items: Array.isArray((q as { items?: string[] }).items)
            ? (q as { items: string[] }).items.map(String)
            : [],
          correctOrder: (q as { correctOrder?: number[] }).correctOrder,
        };
      }
      return {
        ...base,
        kind: 'multiple-choice' as const,
        options: Array.isArray((q as { options?: string[] }).options)
          ? (q as { options: string[] }).options.map(String)
          : [],
        correctIndex: (q as { correctIndex?: number }).correctIndex,
      };
    }),
    settings: {
      shuffleOptions: !!data.settings?.shuffleOptions,
      showCorrectAfter: data.settings?.showCorrectAfter !== false,
      defaultTimeLimitSec: data.settings?.defaultTimeLimitSec ?? 30,
    },
  },
  raw,
  'arena',
);
}

export function createInitialArenaRuntime(payload: ArenaPayload): ArenaRuntime {
  const first = payload.questions[0];
  const limitSec = first?.timeLimitSec ?? payload.settings?.defaultTimeLimitSec ?? 30;
  const now = Date.now();
  return {
    currentQuestionIndex: 0,
    questionStartedAt: now,
    deadlineAt: now + limitSec * 1000,
    answers: {},
    aggregates: {},
    revealed: false,
  };
}

function scoreAnswer(
  ctx: PluginContext<'arena'>,
  participantId: string,
): number {
  const question = ctx.payload.questions[ctx.runtime.currentQuestionIndex];
  if (!question) return 0;
  const answer = ctx.runtime.answers[participantId]?.[question.id];
  if (answer == null) return 0;

  if (question.kind === 'multiple-choice' && typeof question.correctIndex === 'number') {
    return answer === question.correctIndex ? (question.points ?? 100) : 0;
  }
  if (question.kind === 'true-false' && typeof question.correctAnswer === 'boolean') {
    return answer === question.correctAnswer ? (question.points ?? 100) : 0;
  }
  return question.points ?? 50;
}

export const arenaLogic: ActivityLogicPlugin<'arena'> = {
  mode: 'arena',

  validatePayload: validateArenaPayload,
  createInitialRuntime: createInitialArenaRuntime,

  onStudentInput(ctx, input: StudentInputEnvelope, participant: Participant) {
    if (input.action !== 'ANSWER') return { runtime: ctx.runtime };

    if (ctx.phase !== 'ACTIVE') {
      return { runtime: ctx.runtime, rejected: true, rejectReason: 'NOT_ACTIVE' };
    }

    if (ctx.runtime.revealed) {
      return { runtime: ctx.runtime, rejected: true, rejectReason: 'REVEALED' };
    }

    const question = ctx.payload.questions[ctx.runtime.currentQuestionIndex];
    if (!question) return { runtime: ctx.runtime };

    if (ctx.runtime.deadlineAt && Date.now() > ctx.runtime.deadlineAt) {
      return { runtime: ctx.runtime, rejected: true, rejectReason: 'TIME_EXPIRED' };
    }

    const data = input.data as { questionId?: string; value?: unknown };
    if (data.questionId !== question.id) return { runtime: ctx.runtime };

    const answers = { ...ctx.runtime.answers };
    const participantAnswers = { ...(answers[participant.participantId] || {}) };

    if (participantAnswers[question.id] !== undefined) {
      return { runtime: ctx.runtime, rejected: true, rejectReason: 'ALREADY_ANSWERED' };
    }

    participantAnswers[question.id] = data.value;
    answers[participant.participantId] = participantAnswers;

    const aggregates = { ...ctx.runtime.aggregates };
    if (question.kind === 'multiple-choice' && typeof data.value === 'number') {
      const key = String(ctx.runtime.currentQuestionIndex);
      const optionCount = question.options.length;
      const bucket = [...((aggregates[key] as number[]) || Array(optionCount).fill(0))];
      while (bucket.length < optionCount) bucket.push(0);
      bucket[data.value] = (bucket[data.value] || 0) + 1;
      aggregates[key] = bucket;
    }

    const delta = scoreAnswer({ ...ctx, runtime: { ...ctx.runtime, answers } }, participant.participantId);

    return {
      runtime: { ...ctx.runtime, answers, aggregates },
      scores: delta
        ? [{ participantId: participant.participantId, teamId: participant.teamId, delta, reason: 'arena_correct' }]
        : undefined,
    };
  },

  onHostAction(ctx, action: HostActionPayload) {
    if (action.action === 'NEXT_QUESTION') {
      const next = Math.min(ctx.runtime.currentQuestionIndex + 1, ctx.payload.questions.length - 1);
      const question = ctx.payload.questions[next];
      const limitSec = question?.timeLimitSec ?? ctx.payload.settings?.defaultTimeLimitSec ?? 30;
      const now = Date.now();
      return {
        runtime: {
          ...ctx.runtime,
          currentQuestionIndex: next,
          questionStartedAt: now,
          deadlineAt: now + limitSec * 1000,
          revealed: false,
        },
      };
    }
    if (action.action === 'REVEAL') {
      return { runtime: { ...ctx.runtime, revealed: true } };
    }
    return { runtime: ctx.runtime };
  },

  computeScores(ctx, participants): ScorePatch[] {
    return participants
      .map((p) => ({
        participantId: p.participantId,
        teamId: p.teamId,
        delta: scoreAnswer(ctx, p.participantId),
        reason: 'arena_final',
      }))
      .filter((s) => s.delta > 0);
  },
};

export function demoArenaPayload(): ArenaPayload {
  // Neutro — sin capitales de Chile ni aritmética fija (fuga cross-subject).
  return {
    title: 'Arena — repaso rápido',
    questions: [
      {
        id: 'q1',
        kind: 'multiple-choice',
        prompt: '¿Cuál opción describe mejor la idea central del tema?',
        options: ['Idea A', 'Idea B', 'Idea C'],
        correctIndex: 0,
        timeLimitSec: 20,
      },
      {
        id: 'q2',
        kind: 'multiple-choice',
        prompt: '¿Qué paso sigue en la secuencia de trabajo?',
        options: ['Paso previo', 'Paso siguiente', 'Cierre sin práctica'],
        correctIndex: 1,
        timeLimitSec: 15,
      },
    ],
    settings: { showCorrectAfter: true },
  };
}
